import { useState, useCallback, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import Editor from '@monaco-editor/react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Flex from '../../../components/base/Flex';
import OutlinedButton from '../../../components/base/OutlinedButton';
import ContainedButton from '../../../components/base/ContainedButton';
import CustomModal from '../../../components/base/CustomModal';
import CloseIcon from '@mui/icons-material/Close';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import { toast } from 'react-toastify';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ReplayIcon from '@mui/icons-material/Replay';
import { useActiveProject } from '../../../store/projectState';
import { useTheme } from '@emotion/react';
import { useScandiumMutation } from '../../../data-layer/utils';

const UploadAppModal = ({
  open,
  onClose,
  onComplete,
  refetchMobileApps,
  publicKey,
  refetchAppInfo
}) => {
  const activeProject = useActiveProject();
  const theme = useTheme();
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [activeAccordion, setActiveAccordion] = useState(null);
  const [selectedOption, setSelectedOption] = useState('project');

  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState('');
  const [tasks, setTasks] = useState([]);
  const [isCompleting, setIsCompleting] = useState(false);

  const uploadedBytesRef = useRef(0);

  const projectCode = `xcodebuild -project '<project_name>.xcodeproj' \\
    -scheme '<scheme_name>' \\
    -sdk iphonesimulator \\
    -configuration Debug`;
  const workspaceCode = `xcodebuild -workspace '<your_workspace_name>.xcworkspace' \\
    -scheme '<scheme_name>' \\ 
    -sdk iphonesimulator \\ 
    -configuration Debug`;

  const toggleAccordion = (index) => {
    setActiveAccordion((prevActive) => (prevActive === index ? null : index));
  };

  const acceptedFileTypes = ['apk', 'zip', 'tar', 'tz'];
  const allowedFileTypes = [
    'application/vnd.android.package-archive',
    'application/zip',
    'application/x-tar',
    'application/x-gzip'
  ];

  const onDrop = useCallback((acceptedFiles) => {
    if (!acceptedFiles[0] || !allowedFileTypes.includes(acceptedFiles[0].type)) {
      toast.error(
        'Only apps and files with the following extensions are allowed: ' +
          acceptedFileTypes.join(', ')
      );
      return;
    }

    setSelectedFile(acceptedFiles[0]);
    fileInputRef.current = acceptedFiles[0];
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.android.package-archive': ['.apk', '.apks'],
      'application/zip': ['.zip'],
      'application/x-tar': ['.tar'],
      'application/x-gzip': ['.tz']
    },
    maxFiles: 1
  });

  const clearFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      setSelectedFile(null);
      setTasks([]);
      setUploadProgress(0);
      setStatusMessage('');
      setUploading(false);
      setIsCompleting(false);
      uploadedBytesRef.current = 0;
    }
  };

  const { mutateAsync: registerApp, isLoading: isRegisteringApp } = useScandiumMutation(
    `/projects/${activeProject?.id}/mobile-tests/apps/${publicKey ? publicKey : ''}`,
    {
      enabled: !!activeProject?.id && !!selectedFile,
      onError: (error) => {
        toast.error(error.message);
      },
      onSuccess: (data) => {
        toast.success(data.message);
        if (publicKey) {
          refetchAppInfo();
        } else {
          refetchMobileApps();
        }
        onComplete();
      }
    }
  );

  const { mutateAsync: initMultipart } = useScandiumMutation(`/utils/file/multipart/init`, {
    onError: (err) => toast.error(err.message)
  });

  const { mutateAsync: getAllPartUrls } = useScandiumMutation(`/utils/file/multipart/part-urls`, {
    onError: (err) => toast.error(err.message)
  });

  const { mutateAsync: completeMultipart } = useScandiumMutation(`/utils/file/multipart/complete`, {
    onError: (err) => toast.error(err.message)
  });

  const PART_SIZE = 10 * 1024 * 1024;
  const MAX_CONCURRENT = 7;
  const MAX_RETRIES = 3;
  const BACKOFF_BASE_MS = 300;

  const fileSignatureFor = (file) => `${file.name}-${file.size}-${file.lastModified}`;
  const resumeKeyFor = (file) => `multipart-upload:${fileSignatureFor(file)}`;

  const loadResumeState = (file) => {
    try {
      const raw = localStorage.getItem(resumeKeyFor(file));
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };

  const saveResumeState = (file, state) => {
    try {
      localStorage.setItem(resumeKeyFor(file), JSON.stringify(state));
    } catch (err) {
      console.warn('Failed to save multipart state', err);
    }
  };

  const clearResumeState = (file) => {
    try {
      localStorage.removeItem(resumeKeyFor(file));
    } catch {}
  };

  const buildParts = (size, partSize) => {
    const parts = [];
    let offset = 0;
    let partNumber = 1;
    while (offset < size) {
      const end = Math.min(offset + partSize, size);
      parts.push({
        partNumber,
        start: offset,
        end,
        size: end - offset
      });
      partNumber += 1;
      offset = end;
    }
    return parts;
  };

  const sleep = (ms) => new Promise((res) => setTimeout(res, ms));

  // Upload a single part with automatic retries
  const uploadSinglePart = async (task, attempt = 1) => {
    try {
      const resp = await fetch(task.url, {
        method: 'PUT',
        body: task.blob
      });

      if (!resp.ok) {
        const text = await resp.text().catch(() => '');
        throw new Error(`Part upload failed (status ${resp.status}) ${text}`);
      }

      let etag = resp.headers.get('ETag') || resp.headers.get('etag') || '';
      etag = etag.replace(/"/g, '');

      return { PartNumber: task.partNumber, ETag: etag };
    } catch (err) {
      if (attempt <= MAX_RETRIES) {
        const wait = BACKOFF_BASE_MS * Math.pow(2, attempt - 1);
        await sleep(wait);
        return uploadSinglePart(task, attempt + 1);
      }
      throw err;
    }
  };

  // Worker function that processes a task
  const handlePartTask = async ({ task, file, uploadId, key, totalBytes }) => {
    try {
      const result = await uploadSinglePart(task);
      task.status = 'success';
      task.etag = result.ETag;
      task.uploaded = true;

      // update resume state (avoid duplicates)
      const prevState = loadResumeState(file) || { upload_id: uploadId, key, uploadedParts: [] };
      prevState.uploadedParts = prevState.uploadedParts || [];
      if (!prevState.uploadedParts.find((p) => p.PartNumber === task.partNumber)) {
        prevState.uploadedParts.push({
          PartNumber: task.partNumber,
          ETag: result.ETag,
          size: task.size
        });
        saveResumeState(file, prevState);
      }

      uploadedBytesRef.current += task.size;
      const percent = Math.round((uploadedBytesRef.current / totalBytes) * 100);
      setUploadProgress(percent);
    } catch (err) {
      task.status = 'failed';
      console.error('Part failed', task.partNumber, err);
    }
    setTasks((prev) => {
      const copy = [...prev];
      copy[task.partNumber - 1] = { ...task };
      return copy;
    });
  };

  // uploadPartsConcurrently: spawn MAX_CONCURRENT workers that pull next index atomically
  const uploadPartsConcurrently = async (uploadId, key, allPresignedParts, file) => {
    const partsMeta = buildParts(file.size, PART_SIZE);
    const presignMap = new Map(allPresignedParts.map((p) => [Number(p.part_number), p.upload_url]));

    const initialTasks = partsMeta.map((p) => {
      const url = presignMap.get(p.partNumber);
      const blob = file.slice(p.start, p.end);
      return {
        partNumber: p.partNumber,
        url,
        blob,
        size: p.size,
        status: 'pending',
        etag: null,
        uploaded: false
      };
    });

    const resume = loadResumeState(file);
    if (resume && Array.isArray(resume.uploadedParts)) {
      const uploaded = new Map(resume.uploadedParts.map((u) => [u.PartNumber, u]));
      initialTasks.forEach((t) => {
        if (uploaded.has(t.partNumber)) {
          t.status = 'success';
          t.etag = uploaded.get(t.partNumber).ETag;
          t.uploaded = true;
        }
      });
    }

    setTasks(initialTasks);

    uploadedBytesRef.current = initialTasks.reduce((acc, t) => acc + (t.uploaded ? t.size : 0), 0);
    const totalBytes = file.size;
    setUploadProgress(Math.round((uploadedBytesRef.current / totalBytes) * 100));

    setStatusMessage('Uploading...');
    setUploading(true);

    let cursorRef = { value: 0 };
    const totalParts = initialTasks.length;

    const worker = async () => {
      while (true) {
        const idx = cursorRef.value++;
        if (idx >= totalParts) return;
        const task = initialTasks[idx];

        if (task.status === 'success') continue;

        task.status = 'uploading';
        setTasks((prev) => {
          const copy = [...prev];
          copy[task.partNumber - 1] = { ...task };
          return copy;
        });

        await handlePartTask({ task, file, uploadId, key, totalBytes });
      }
    };

    const workers = [];
    const spawnCount = Math.min(MAX_CONCURRENT, totalParts);
    for (let i = 0; i < spawnCount; i++) {
      workers.push(worker());
    }

    await Promise.all(workers);

    setUploading(false);
    return initialTasks;
  };

  const resumeFailedParts = async () => {
    if (!selectedFile) return;
    const resumeState = loadResumeState(selectedFile);
    if (!resumeState || !resumeState.upload_id || !resumeState.key) {
      toast.error('No upload session to resume. Please start upload again.');
      return;
    }

    const failedTasks = tasks.filter((t) => t.status === 'failed');
    if (!failedTasks.length) {
      toast.info('No failed parts to retry.');
      return;
    }

    setStatusMessage('Retrying failed parts...');
    setUploading(true);

    // We'll reuse uploadSinglePart and handlePartTask on only failed tasks.
    // If some tasks have missing url, request part-urls again for all parts (safe)
    const missingUrl = failedTasks.some((t) => !t.url);
    if (missingUrl) {
      try {
        const partsMeta = buildParts(selectedFile.size, PART_SIZE);
        const totalParts = partsMeta.length;
        const presignResp = await getAllPartUrls({
          upload_id: resumeState.upload_id,
          key: resumeState.key,
          total_parts: totalParts
        });
        if (!presignResp?.success || !Array.isArray(presignResp.data)) {
          throw new Error('Failed to re-fetch presigned URLs');
        }
        const presignMap = new Map(
          presignResp.data.map((p) => [Number(p.part_number), p.upload_url])
        );

        setTasks((prev) => {
          const copy = prev.map((t) => {
            if (!t.url && presignMap.has(t.partNumber)) {
              return { ...t, url: presignMap.get(t.partNumber) };
            }
            return t;
          });
          return copy;
        });
      } catch (err) {
        setUploading(false);
        toast.error('Failed to re-fetch presigned URLs: ' + (err.message || ''));
        return;
      }
    }

    const failedTasksCopy = tasks
      .map((t) => (t.status === 'failed' ? { ...t } : t))
      .filter(Boolean);

    const localFailed = tasks.filter((t) => t.status === 'failed');
    if (localFailed.length === 0) {
      setUploading(false);
      setStatusMessage('');
      return;
    }

    let cursorRef = { value: 0 };
    const totalLocal = localFailed.length;

    const localWorker = async () => {
      while (true) {
        const idx = cursorRef.value++;
        if (idx >= totalLocal) return;
        const task = localFailed[idx];
        task.status = 'uploading';
        setTasks((prev) => {
          const copy = [...prev];
          copy[task.partNumber - 1] = { ...task };
          return copy;
        });

        await handlePartTask({
          task,
          file: selectedFile,
          uploadId: resumeState.upload_id,
          key: resumeState.key,
          totalBytes: selectedFile.size
        });
      }
    };

    const workers = [];
    const spawnCount = Math.min(MAX_CONCURRENT, totalLocal);
    for (let i = 0; i < spawnCount; i++) workers.push(localWorker());
    await Promise.all(workers);

    setUploading(false);

    const stillFailed = tasks.filter((t) => t.status === 'failed');
    if (stillFailed.length > 0) {
      toast.warn(`${stillFailed.length} part(s) still failed. Click Retry to try again.`);
      setStatusMessage('Some parts still failed');
      return;
    }

    await finalizeMultipartAndRegister();
  };

  // finalize multipart -> call complete endpoint, then register app
  const finalizeMultipartAndRegister = async () => {
    try {
      setIsCompleting(true);
      setStatusMessage('Finalizing upload...');

      const resumeState = loadResumeState(selectedFile);
      if (!resumeState || !Array.isArray(resumeState.uploadedParts)) {
        throw new Error('No uploaded parts recorded; upload incomplete');
      }

      const partsForComplete = resumeState.uploadedParts
        .map((p) => ({ ETag: p.ETag, PartNumber: p.PartNumber }))
        .sort((a, b) => a.PartNumber - b.PartNumber);

      const completeResp = await completeMultipart({
        upload_id: resumeState.upload_id,
        key: resumeState.key,
        parts: partsForComplete
      });

      if (!completeResp?.success) {
        throw new Error('Multipart complete failed');
      }

      const file_url = completeResp.data?.file_url;

      const isAndroid = selectedFile.name.endsWith('.apk') || selectedFile.name.endsWith('.apks');
      const appType = isAndroid ? 'android' : 'ios';

      await registerApp({
        file_url,
        app_type: appType
      });

      clearResumeState(selectedFile);
      setUploadProgress(100);
      setStatusMessage('Upload complete');
      clearFileInput();
    } catch (err) {
      console.error('Finalize error', err);
      toast.error(err.message || 'Failed to finalize upload');
    } finally {
      setIsCompleting(false);
      setUploading(false);
    }
  };

  const handleUploadMobileApp__ = async (e) => {
    e?.preventDefault?.();

    if (!selectedFile) {
      toast.error('Please select a file before saving');
      return;
    }
    if (!activeProject?.id) {
      toast.error('No active project selected');
      return;
    }

    try {
      setUploadProgress(0);
      setStatusMessage('Initializing upload...');
      setUploading(true);

      let uploadId;
      let key;

      const resumeState = loadResumeState(selectedFile);
      if (resumeState && resumeState.upload_id && resumeState.key) {
        uploadId = resumeState.upload_id;
        key = resumeState.key;
        setStatusMessage('Resuming upload...');
      } else {
        const folder = `mobile-apps/${activeProject.id}`;
        const initResp = await initMultipart({
          folder,
          filename: selectedFile.name
        });

        if (!initResp?.success) throw new Error('Failed to initialize multipart upload');

        uploadId = initResp.data.upload_id;
        key = initResp.data.key;

        saveResumeState(selectedFile, {
          upload_id: uploadId,
          key,
          uploadedParts: []
        });
      }

      const partsMeta = buildParts(selectedFile.size, PART_SIZE);
      const totalParts = partsMeta.length;

      setStatusMessage('Requesting presigned urls for parts...');

      const presignResp = await getAllPartUrls({
        upload_id: uploadId,
        key,
        total_parts: totalParts
      });

      if (!presignResp?.success || !Array.isArray(presignResp.data)) {
        throw new Error('Failed to get presigned URLs for parts');
      }

      const allPresignedParts = presignResp.data;

      const uploadedTasks = await uploadPartsConcurrently(
        uploadId,
        key,
        allPresignedParts,
        selectedFile
      );

      const failedParts = uploadedTasks.filter((t) => t.status === 'failed');

      if (failedParts.length > 0) {
        setStatusMessage(
          'Some parts failed. Click "Retry failed parts" to resume partial uploads.'
        );
        setUploading(false);
        toast.warn(`${failedParts.length} part(s) failed. Click retry to re-upload failed parts.`);
        return;
      }

      await finalizeMultipartAndRegister();
    } catch (error) {
      console.error('Multipart upload failed', error);
      setStatusMessage('Upload failed: ' + (error.message || 'unknown'));
      setUploading(false);
      toast.error(error.message || 'Failed to upload app');
    }
  };

  const editorOptions = {
    fontSize: 12,
    formatOnPaste: true,
    readOnly: true,
    renderIndentGuides: false,
    lineNumbers: false,
    formatOnType: true,
    inlineSuggest: false,
    autoClosingBrackets: true,
    wordWrap: 'on',
    minimap: {
      enabled: false
    }
  };

  const failedTasks = tasks.filter((t) => t.status === 'failed');
  const hasFailed = failedTasks.length > 0;
  const isUploadingOrCompleting = uploading || isCompleting;

  return (
    <CustomModal open={open}>
      <Box
        component={'form'}
        onSubmit={handleUploadMobileApp__}
        encType={'multipart/form-data'}
        py={1}
        px={2}
        width={'80vw'}
        maxWidth={'680px'}
        position={'relative'}>
        <Typography
          as={'h4'}
          color="primary"
          fontWeight={600}
          sx={{
            fontSize: '1.4rem',
            mb: 1
          }}>
          {publicKey ? 'Upload Your App Latest Build' : 'Upload Your App File'}
        </Typography>

        <Typography variant="body">
          For{' '}
          <Typography component={'span'} fontWeight={500}>
            iOS
          </Typography>
          , upload a{' '}
          <Typography component={'span'} color={'primary'} fontWeight={500}>
            .zip, .tar
          </Typography>{' '}
          or{' '}
          <Typography component={'span'} color={'primary'} fontWeight={500}>
            .gz
          </Typography>
          . file containing your compressed{' '}
          <Typography component={'span'} color={'primary'} fontWeight={500}>
            .app
          </Typography>{' '}
          bundle.
        </Typography>

        <Box mb={2} sx={{ bgcolor: theme.palette.background.reportCard, py: 1.5 }}>
          <Typography
            variant="body"
            color={'primary'}
            onClick={() => toggleAccordion(1)}
            sx={{ cursor: 'pointer' }}>
            {activeAccordion === 1
              ? 'Hide detailed iOS instructions'
              : 'Show detailed iOS instructions'}
          </Typography>
          {activeAccordion === 1 && (
            <Box>
              <Typography variant="body">
                Your .app must represent a simulator build of your app. After running the iOS
                simulator in Xcode, you can find your .app bundle by navigating to{' '}
                <Typography fontWeight={500} component={'span'}>
                  Product &rarr; Show Build Folder in Finder &rarr; Products/Debug-iphonesimulator.
                </Typography>
              </Typography>

              <Typography component={'div'} variant="body" sx={{ mt: 1 }}>
                Alternatively, you can run the following command in your project directory:
              </Typography>

              <div className="button-container">
                <button
                  className={`option-button ${selectedOption === 'project' ? 'active' : ''}`}
                  onClick={(event) => {
                    event.preventDefault();
                    setSelectedOption('project');
                  }}>
                  Using .xcodeproj
                </button>
                <button
                  className={`option-button ${selectedOption === 'workspace' ? 'active' : ''}`}
                  onClick={(event) => {
                    event.preventDefault();
                    setSelectedOption('workspace');
                  }}>
                  Using .xcworkspace
                </button>
              </div>
              <Editor
                height={'80px'}
                theme={'vs-dark'}
                value={selectedOption === 'project' ? projectCode : workspaceCode}
                language={'shell'}
                options={editorOptions}
              />

              <Typography component={'div'} variant="body" sx={{ mt: 1 }}>
                You can then zip the .app bundle found in{' '}
                <Typography fontWeight={500} component={'span'}>
                  build/Debug-iphonesimulator/.
                </Typography>
              </Typography>

              <Typography component={'div'} variant="body" sx={{ mt: 1 }}>
                For more information, please see our{' '}
                <a
                  href="https://docs.getscandium.com/mobile-application-testing/uploading-apps/ios"
                  target="_blank"
                  rel="noreferrer">
                  docs
                </a>{' '}
                for iOS
              </Typography>
            </Box>
          )}
        </Box>

        <Typography
          variant="body"
          sx={{
            mt: 1
          }}>
          For{' '}
          <Typography component={'span'} fontWeight={500}>
            Android
          </Typography>
          , upload the{' '}
          <Typography component={'span'} color={'primary'} fontWeight={500}>
            .apk
          </Typography>{' '}
          containing your app.
        </Typography>

        <Box mb={2} sx={{ bgcolor: theme.palette.background.reportCard, py: 1.5 }}>
          <Typography
            variant="body"
            color={'primary'}
            onClick={() => toggleAccordion(2)}
            sx={{ cursor: 'pointer' }}>
            {activeAccordion === 2
              ? 'Hide detailed Android instructions'
              : 'Show detailed Android instructions'}
          </Typography>
          {activeAccordion === 2 && (
            <Box>
              <Typography variant="body">
                You can build your app via Android Studio, or by running the following command in
                your project directory:{' '}
              </Typography>

              <Editor
                height={'20px'}
                theme={'vs-dark'}
                value={'./gradlew <assembleDebug | assembleRelease>'}
                language={'shell'}
                options={editorOptions}
              />

              <Typography component={'div'} variant="body" sx={{ mt: 1 }}>
                You can then find the .apk file in:{' '}
                <Typography fontWeight={500} component={'span'}>
                  &lt;project-name&gt;/&lt;module-name&gt;/build/outputs/apk/.
                </Typography>
              </Typography>

              <Typography component={'div'} variant="body" sx={{ mt: 1 }}>
                For more information, please see our{' '}
                <a
                  href="https://docs.getscandium.com/mobile-application-testing/uploading-apps/android"
                  target="_blank"
                  rel="noreferrer">
                  docs
                </a>{' '}
                for Android
              </Typography>
            </Box>
          )}
        </Box>

        <Flex
          {...getRootProps()}
          sx={{
            bgcolor: theme.palette.background.reportCard,
            mt: 1,
            py: 3,
            cursor: 'pointer',
            justifyContent: 'center'
          }}>
          <input {...getInputProps()} />
          {isDragActive ? (
            <Typography fontSize={'0.9rem'}>Drop the file here ...</Typography>
          ) : (
            <Flex sx={{ flexDirection: 'column', gap: 1 }}>
              <CloudUploadIcon color={'primary'} sx={{ fontSize: '4rem' }} />
              <Typography fontSize={'0.95rem'}>
                Drag and Drop File here, or click to select one.
              </Typography>
            </Flex>
          )}
        </Flex>

        {selectedFile && (
          <Flex flexWrap={'wrap'}>
            <Typography as={'h6'} sx={{ fontSize: '0.9rem' }}>
              {selectedFile?.name}
            </Typography>
            <Tooltip title={'Delete the selected file'}>
              <IconButton size={'small'} onClick={clearFileInput}>
                <CloseIcon color={'error'} />
              </IconButton>
            </Tooltip>
          </Flex>
        )}

        {uploading || isCompleting ? (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2">Status: {statusMessage}</Typography>
            <Box sx={{ width: '100%', background: '#eee', height: 8, borderRadius: 2, mt: 1 }}>
              <Box
                sx={{
                  width: `${uploadProgress}%`,
                  height: '100%',
                  bgcolor: 'primary.main',
                  borderRadius: 2
                }}
              />
            </Box>
            <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
              {uploadProgress}%
            </Typography>
          </Box>
        ) : null}

        {hasFailed && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Some parts failed to upload.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Tooltip title="Retry failed parts">
                <IconButton
                  size="small"
                  onClick={resumeFailedParts}
                  aria-label="retry failed parts"
                  disabled={uploading || isCompleting}>
                  <ReplayIcon />
                </IconButton>
              </Tooltip>
              <Typography variant="caption">Click to retry failed parts</Typography>
            </Box>
          </Box>
        )}

        <Flex sx={{ mt: 3 }} columnGap={2} justifyContent={'flex-end'}>
          <OutlinedButton
            onClick={() => {
              onClose();
              clearFileInput();
            }}>
            Cancel
          </OutlinedButton>
          <Tooltip title={!selectedFile ? 'Select a file before saving' : ''}>
            <ContainedButton
              disabled={!selectedFile || isRegisteringApp || uploading || isCompleting}
              isLoading={isRegisteringApp || uploading || isCompleting}
              type={'submit'}
              color={'primary'}
              sx={{
                '&:disabled': {
                  cursor: 'not-allowed',
                  pointerEvents: 'all !important'
                }
              }}
              autoFocus>
              Save
            </ContainedButton>
          </Tooltip>
        </Flex>
      </Box>
    </CustomModal>
  );
};

export default UploadAppModal;
